// Mahjong Connect: the line that joined the pair just taken, drawn over the board for a moment while the two tiles fly
// to the discard pile (owner 2026-10-31). Found from the position before the move (the last undo snapshot): the two
// places that emptied, joined through the tiles that stood there then. Read from the engine state alone, so an undo,
// a shuffle or a deal draws nothing. The grid's pixels come from the CSS variables geometry.ts writes on the board.
import { useEffect, useRef, useState, type RefObject } from 'react';
import { connectLayoutOf, connectPath, type Cell } from '../engine/rules/mahjongConnect';
import { useEngineState } from '../store/gameStore';

/** How long the line stays (about the tiles' flight). */
const TRAIL_MS = 650;

interface Trail {
  readonly key: number;
  readonly points: string;
  readonly width: number;
}

export function ConnectTrail({ innerRef }: { innerRef: RefObject<HTMLDivElement | null> }) {
  const s = useEngineState();
  const [trail, setTrail] = useState<Trail | null>(null);
  const seen = useRef(s);
  const counter = useRef(0);
  // the hide timer lives outside the effect: the clock's tick changes the state every second, and a cleanup returned
  // from this effect would then cancel the hide before it fired
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    const prev = seen.current;
    seen.current = s;
    const inner = innerRef.current;
    if (!inner || prev.game !== s.game || prev.deals !== s.deals) return;
    const before = prev.piles.foundations[0]?.length ?? 0;
    const after = s.piles.foundations[0]?.length ?? 0;
    if (after !== before + 2 || s.history.length !== prev.history.length + 1) return;
    const gone = prev.piles.tableau.map((p, i) => (p.length && !s.piles.tableau[i]!.length ? i : -1)).filter((i) => i >= 0);
    if (gone.length !== 2) return;
    const [a, b] = gone as [number, number];
    const path = connectPath(connectLayoutOf(prev), a, b, (i) => i !== a && i !== b && !!prev.piles.tableau[i]?.length);
    if (!path) return;
    const style = getComputedStyle(inner);
    const px = (name: string) => parseFloat(style.getPropertyValue(name)) || 0;
    const x0 = px('--tableau-x0');
    const y0 = px('--tableau-y');
    const hw = px('--tile-hw');
    const hh = px('--tile-hh');
    // a cell's centre: its top left corner in half tiles (board.css --col / --row) plus half a face
    const at = (c: Cell) => `${x0 + (2 * c.c + 1) * hw},${y0 + (2 * c.r + 1) * hh}`;
    counter.current += 1;
    setTrail({ key: counter.current, points: path.map(at).join(' '), width: Math.max(3, Math.round(hw * 0.16)) });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setTrail(null), TRAIL_MS);
  }, [s, innerRef]);
  if (!trail) return null;
  return (
    <svg key={trail.key} className="connect_trail" aria-hidden="true">
      <polyline className="connect_trail__shade" points={trail.points} style={{ strokeWidth: trail.width + 4 }} />
      <polyline className="connect_trail__line" points={trail.points} style={{ strokeWidth: trail.width }} />
    </svg>
  );
}
